/* Paid-only Shopify cart transport. Campaign rewards remain a separate provider. */
(() => {
  if (window.TheManPaidCart) return;
  const journalKey = 'tm-bb:pending-paid:v1';
  let queue = Promise.resolve(), recovery, lastStatus;
  const root = () => window.Shopify?.routes?.root || '/';
  const uncertain = message => Object.assign(new Error(message || 'CHECK_BAG_BEFORE_RETRY'), { uncertain: true });
  const storageMessage = 'Your browser cannot save purchase recovery information. Review your bag before adding more.';
  const read = () => {
    const raw = sessionStorage.getItem(journalKey);
    if (!raw) return null;
    if (raw.length > 32000) throw uncertain();
    const op = JSON.parse(raw);
    if (op.version !== 1 || !op.id || !op.before || !Array.isArray(op.lines) || !op.lines.length || op.lines.length > 100) throw uncertain();
    return op;
  };
  const publish = state => {
    lastStatus = state;
    document.dispatchEvent(new CustomEvent('tm:paid-cart-state', { detail: state }));
    return state;
  };
  const status = () => {
    try { const op = read(); return op ? (lastStatus?.blocked ? lastStatus : { blocked: true, message: 'An earlier addition needs checking. Review your bag or check again before adding more.' }) : { blocked: false, message: '' }; }
    catch (_) { return { blocked: true, message: storageMessage }; }
  };
  const save = op => {
    const raw = JSON.stringify(op);
    if (raw.length > 32000) throw uncertain(storageMessage);
    sessionStorage.setItem(journalKey, raw);
    if (sessionStorage.getItem(journalKey) !== raw) throw uncertain(storageMessage);
  };
  const clear = () => {
    sessionStorage.removeItem(journalKey);
    if (sessionStorage.getItem(journalKey) !== null) throw uncertain(storageMessage);
  };
  const request = async (path, options = {}) => {
    const response = await fetch(root() + path, { credentials: 'same-origin', cache: 'no-store', ...options, signal: AbortSignal.timeout(15000) });
    const data = await response.json();
    if (!response.ok) throw Object.assign(new Error(data.description || 'CART_REQUEST_FAILED'), { rejected: response.status >= 400 && response.status < 500 });
    return data;
  };
  const reconcile = async op => {
    const cart = await request('cart.js');
    const delta = window.TheManBundleState.cartDelta(op.before, cart, op.lines);
    if (cart.currency === op.currency && delta.every(l => l.added === l.requested)) {
      clear();
      publish({ blocked: false, confirmed: true, message: '' });
      return { confirmed: true, before: op.before, cart, mode: 'native-paid' };
    }
    const comparable = cart.currency === op.currency && delta.every(l => l.added >= 0 && l.added <= l.requested);
    const changed = comparable ? `Last checked: ${delta.reduce((n, l) => n + l.added, 0)} of ${op.lines.reduce((n, l) => n + l.quantity, 0)} requested units added. ` : 'Your bag has changed since this addition started. ';
    publish({ blocked: true, message: changed + 'Review your bag or check again. Nothing will be resent automatically.' });
    return { confirmed: false, before: op.before, cart, delta };
  };
  const locked = task => {
    if (window.TheManCartCoordinator) return window.TheManCartCoordinator.mutate(task);
    const run = () => navigator.locks ? navigator.locks.request('the-man-paid-cart', task) : task();
    const result = queue.then(run); queue = result.catch(() => {}); return result;
  };
  const transfer = async ({ lines, currency }) => {
    if (!lines.length || lines.length > 100 || new Set(lines.map(l => String(l.id))).size !== lines.length || lines.some(l => !/^\d+$/.test(String(l.id)) || !Number.isSafeInteger(l.quantity) || l.quantity < 1)) throw new Error('INVALID_LINES');
    // Never overwrite an unresolved operation, including after a reload/reconnect.
    let prior;
    try { prior = read(); } catch (_) { throw uncertain(storageMessage); }
    if (prior) {
      try { await reconcile(prior); } catch (_) { publish(status()); }
      throw uncertain('CHECK_BAG_BEFORE_RETRY');
    }
    const cartBefore = await request('cart.js');
    if (cartBefore.currency !== currency) throw new Error('CURRENCY_CHANGED');
    // Only the matching plain merchandise counts are needed. No cart token, names,
    // addresses, notes, custom properties, prices or unrelated line data are saved.
    const before = { currency, items: cartBefore.items.filter(l => lines.some(r => String(r.id) === String(l.variant_id || l.id)) && !Object.keys(l.properties || {}).length && !l.selling_plan_allocation).map(l => ({ variant_id: String(l.variant_id || l.id), quantity: l.quantity })) };
    const op = { version: 1, id: crypto.randomUUID(), createdAt: Date.now(), currency, lines: lines.map(({ id, quantity }) => ({ id: String(id), quantity })), before };
    try { save(op); } catch (_) { publish({ blocked: true, message: storageMessage }); throw uncertain(storageMessage); }
    publish({ blocked: true, message: 'Checking your addition. Please wait before adding again.' });
    let failure;
    try {
      await request('cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: op.lines }) });
    } catch (error) { failure = error; }
    let result;
    try { result = await reconcile(op); } catch (_) { publish(status()); throw uncertain(); }
    if (result.confirmed) return result;
    // A definitive rejection with a verified zero delta is safe to try anew.
    if (failure?.rejected && result.cart.currency === currency && result.delta.every(l => l.added === 0)) {
      clear(); publish({ blocked: false, message: failure.message }); throw failure;
    }
    // Keep the journal until verified completion. Time alone never permits retry.
    throw uncertain();
  };
  window.TheManPaidCart = {
    version: 1,
    status,
    transfer(input) { return locked(() => transfer(input)); },
    recover() {
      if (recovery) return recovery;
      recovery = locked(async () => {
        try { const op = read(); return op ? await reconcile(op) : publish({ blocked: false, message: '' }); }
        catch (_) { return publish(status()); }
      }).catch(() => publish(status())).finally(() => { recovery = null; });
      return recovery;
    },
    async open(opener) {
      if (window.theme?.cartDrawer) {
        try {
          const drawer = window.theme.cartDrawer;
          if (window.theme.cart?.getCartProductMarkup && drawer.cartForm.cartMarkup) {
            const markup = await window.theme.cart.getCartProductMarkup();
            drawer.cartForm.cartMarkup(markup);
          } else {
            await drawer.cartForm.buildCart();
          }
          const visibleOpener = opener?.isConnected && opener.getClientRects().length > 0 && !opener.closest('[inert], [aria-hidden="true"]') ? opener : null;
          if (visibleOpener && drawer.drawer) {
            visibleOpener.setAttribute('aria-expanded', 'true');
            drawer.drawer.activeSource = visibleOpener;
          }
          drawer.open(visibleOpener);
          return;
        } catch (_) {}
      }
      location.assign(root() + 'cart');
    }
  };
})();
